import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { readPage, readItem } from '../../../utils/apiResult';

const POTENTIAL_STATUSES = ['completed'];

const usePotentialAssessmentApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  // TD-23 — bkz. utils/apiResult.js: GET okumaları reject etmez, sinyal res.data.status.
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/potential-assessments?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: page } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(page);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const upsert = useCallback(async (payload) => {
    try {
      await api.post('/v1/potential-assessments', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // TD-23 — başarısız okumada açıkça null (bkz. utils/apiResult.js).
  const getById = useCallback(async (id) => {
    const res = await api.get(`/v1/potential-assessments/${id}`);
    return readItem(res);
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/potential-assessments/${ids[0]}`);
      } else {
        await Promise.all(ids.map((id) => api.delete(`/v1/potential-assessments/${id}`)));
      }
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.potential.employee'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'overallScore',
      label: t('performance.potential.overall'),
      options: { filter: false, sort: true },
    },
    {
      name: 'status',
      label: t('performance.potential.status'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => (v ? t(`performance.potential.statusValue.${v}`) : '-'),
        filterOptions: {
          defaultOp: 'in',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.potential.status')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => {
                  fl[i] = e.target.value ? [e.target.value] : [];
                  onChange(fl[i], i, col);
                }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {POTENTIAL_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>{t(`performance.potential.statusValue.${s}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    dateFilterColumn('createDate', t('performance.potential.createDate'), lang),
  ], [t, lang]);

  return { columns, data, fetching, pagination, loadFailed, upsert, getById, _delete, refresh };
};

export default usePotentialAssessmentApi;
